import {Component, Input, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {ConfirmationService} from 'primeng/api';
import {TerceroUpPasarelaContactoService} from '../../../../../_services/tercero-up-pasarela-contacto.service';
import {PlataformaDigitalService} from '../../../../../_services/plataforma-digital.service';
import {NavService} from '../../../../../_services/_nav.service';
import {TerceroUPPasarelaContacto} from '../../../../../_models/tercero-up-pasarela-contacto';
import {RespuestaGenerica} from '../../../../../_models/respuesta-generica';
import {CodigosMetodosEnvios} from '../../../../../_enums/enum';
import {toCapitalize} from '../../../../../utils/utilidades';
import {filtrarPorTexto} from '../../admsb-titular.helpers';

const REGEX_CORREO = '[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,3}(\\.[a-zA-Z]{2,3})?$';

@Component({
  selector: 'app-admsb-contactos-tabla',
  templateUrl: './admsb-contactos-tabla.component.html',
  styleUrls: ['./admsb-contactos-tabla.component.scss'],
})
export class AdmsbContactosTablaComponent implements OnInit {

  /** Identificador de la pasarela del titular (idTerceroUPPasarela) */
  @Input() idTitular: string;

  contactos: TerceroUPPasarelaContacto[] = [];
  cargandoContactos = true;

  /** Texto del buscador de la tabla de contactos */
  filtroContactos = '';

  /** Controla la visibilidad del sidebar de agregar */
  sidebarVisible = false;

  formulario: FormGroup;
  submitted = false;
  guardandoContacto = false;

  /** Estado de verificación OTP por canal (independiente del formulario) */
  telefonoVerificado = false;
  correoVerificado = false;
  solicitandoCodigoTelefono = false;
  solicitandoCodigoCorreo = false;
  confirmandoTelefono = false;
  confirmandoCorreo = false;

  /** hashOtp devuelto por generarOtpValidacionIdentidad, requerido para validar el código */
  hashOtpTelefono: string | null = null;
  hashOtpCorreo: string | null = null;

  constructor(
    private fb: FormBuilder,
    private terceroUpPasarelaContactoServicio: TerceroUpPasarelaContactoService,
    private plataformaDigitalServicio: PlataformaDigitalService,
    private navService: NavService,
    private confirmService: ConfirmationService,
  ) {
    this.formulario = this.fb.group({
      nombreContacto: [null, [Validators.required]],
      incluirTelefono: [false],
      incluirCorreo: [false],
      numeroTelefono: [null],
      correoElectronico: [null],
    });
  }

  ngOnInit(): void {
    if (this.idTitular) {
      this.cargarContactos(this.idTitular);
    } else {
      this.cargandoContactos = false;
    }
  }

  private cargarContactos(idTerceroUPPasarela: string): void {
    this.terceroUpPasarelaContactoServicio.listContactosByPasarela(idTerceroUPPasarela).subscribe({
      next: res => {
        this.contactos = res;
        this.cargandoContactos = false;
      },
      error: () => this.cargandoContactos = false,
    });
  }

  /**
   * Contactos habilitados, filtrados localmente por nombre, teléfono o correo según el texto del buscador.
   * Los registros con indicadorHabilitado en false no se muestran en la tabla.
   */
  get contactosFiltrados(): TerceroUPPasarelaContacto[] {
    return filtrarPorTexto(this.contactos.filter(c => c.indicadorHabilitado), this.filtroContactos,
      [c => c.nombreContacto, c => c.numeroTelefono, c => c.correoElectronico]);
  }

  /**
   * Habilita "Guardar" solo cuando el nombre es válido, hay al menos un canal marcado
   * y todos los canales marcados quedaron verificados por OTP.
   */
  get puedeGuardar(): boolean {
    const v = this.formulario.value;
    const algunCanal = v.incluirTelefono || v.incluirCorreo;
    const telOk = !v.incluirTelefono || this.telefonoVerificado;
    const correoOk = !v.incluirCorreo || this.correoVerificado;
    return this.formulario.get('nombreContacto').valid && algunCanal && telOk && correoOk;
  }

  abrirSidebarAgregar(): void {
    this.submitted = false;
    this.guardandoContacto = false;
    this.reiniciarVerificacionTelefono();
    this.reiniciarVerificacionCorreo();
    this.formulario.reset({incluirTelefono: false, incluirCorreo: false});
    this.limpiarValidadoresCanal('numeroTelefono');
    this.limpiarValidadoresCanal('correoElectronico');
    this.sidebarVisible = true;
  }

  cerrarSidebar(): void {
    this.sidebarVisible = false;
  }

  capitalizarNombre(): void {
    const ctrl = this.formulario.get('nombreContacto');
    if (ctrl.value) {
      ctrl.setValue(toCapitalize(ctrl.value));
    }
  }

  onToggleTelefono(): void {
    if (this.formulario.value.incluirTelefono) {
      this.formulario.get('numeroTelefono').setValidators([Validators.required, Validators.pattern(/^3\d{9}$/)]);
    } else {
      this.limpiarValidadoresCanal('numeroTelefono');
      this.reiniciarVerificacionTelefono();
    }
    this.formulario.get('numeroTelefono').updateValueAndValidity();
  }

  onToggleCorreo(): void {
    if (this.formulario.value.incluirCorreo) {
      this.formulario.get('correoElectronico').setValidators([Validators.required, Validators.pattern(REGEX_CORREO)]);
    } else {
      this.limpiarValidadoresCanal('correoElectronico');
      this.reiniciarVerificacionCorreo();
    }
    this.formulario.get('correoElectronico').updateValueAndValidity();
  }

  private limpiarValidadoresCanal(control: string): void {
    const ctrl = this.formulario.get(control);
    ctrl.setValue(null);
    ctrl.clearValidators();
    ctrl.updateValueAndValidity();
  }

  onCambioTelefono(): void {
    if (this.telefonoVerificado || this.hashOtpTelefono) {
      this.reiniciarVerificacionTelefono();
    }
  }

  onCambioCorreo(): void {
    if (this.correoVerificado || this.hashOtpCorreo) {
      this.reiniciarVerificacionCorreo();
    }
  }

  private reiniciarVerificacionTelefono(): void {
    this.telefonoVerificado = false;
    this.hashOtpTelefono = null;
  }

  private reiniciarVerificacionCorreo(): void {
    this.correoVerificado = false;
    this.hashOtpCorreo = null;
  }

  private telefonoDuplicado(valor: string): boolean {
    return this.contactos.some(c => c.indicadorHabilitado && c.numeroTelefono === valor);
  }

  private correoDuplicado(valor: string): boolean {
    return this.contactos.some(c => c.indicadorHabilitado &&
      (c.correoElectronico ?? '').toLowerCase() === (valor ?? '').toLowerCase());
  }

  private mostrarDuplicado(): void {
    this.navService.setMesage(0, {
      severity: 'info', summary: this.navService.buscarMensaje('LBL_INFORMACION'),
      detail: this.navService.buscarMensaje('ADMINISTRADORSERVICIOSBANCARIOS.MSG_CONTACTODUPLICADO'),
    });
  }

  solicitarCodigoTelefono(): void {
    const ctrl = this.formulario.get('numeroTelefono');
    ctrl.markAsTouched();
    if (ctrl.invalid) {
      return;
    }
    if (this.telefonoDuplicado(ctrl.value)) {
      this.mostrarDuplicado();
      return;
    }
    this.solicitandoCodigoTelefono = true;
    this.plataformaDigitalServicio.generarOtpValidacionIdentidad({
      codigoMedio: CodigosMetodosEnvios.OTP,
      correo: null, numeroCelular: ctrl.value,
    }).subscribe({
      next: (res: RespuestaGenerica) => this.procesarRespuestaGenerar(res, 'telefono'),
      error: () => {
        this.solicitandoCodigoTelefono = false;
        this.navService.setMesage(3);
      },
    });
  }

  solicitarCodigoCorreo(): void {
    const ctrl = this.formulario.get('correoElectronico');
    ctrl.markAsTouched();
    if (ctrl.invalid) {
      return;
    }
    if (this.correoDuplicado(ctrl.value)) {
      this.mostrarDuplicado();
      return;
    }
    this.solicitandoCodigoCorreo = true;
    this.plataformaDigitalServicio.generarOtpValidacionIdentidad({
      codigoMedio: CodigosMetodosEnvios.CORREO,
      correo: ctrl.value, numeroCelular: null,
    }).subscribe({
      next: (res: RespuestaGenerica) => this.procesarRespuestaGenerar(res, 'correo'),
      error: () => {
        this.solicitandoCodigoCorreo = false;
        this.navService.setMesage(3);
      },
    });
  }

  private procesarRespuestaGenerar(res: RespuestaGenerica, canal: 'telefono' | 'correo'): void {
    if (canal === 'telefono') {
      this.solicitandoCodigoTelefono = false;
    } else {
      this.solicitandoCodigoCorreo = false;
    }
    if (res?.estado) {
      if (canal === 'telefono') {
        this.hashOtpTelefono = res.mensaje;
        this.telefonoVerificado = false;
      } else {
        this.hashOtpCorreo = res.mensaje;
        this.correoVerificado = false;
      }
      this.navService.setMesage(0, {
        severity: 'success', summary: this.navService.buscarMensaje('LBL_INFORMACION'),
        detail: this.navService.buscarMensaje('ADMINISTRADORSERVICIOSBANCARIOS.MSG_CODIGOENVIADO'),
      });
    } else {
      this.navService.setMesage(4, {severity: 'error', summary: 'Error', detail: res?.mensaje});
    }
  }

  confirmarCodigoTelefono(codigo: string): void {
    if (!this.hashOtpTelefono) {
      return;
    }
    this.confirmandoTelefono = true;
    this.plataformaDigitalServicio.validarOtpValidacionIdentidad({hashOtp: this.hashOtpTelefono, codigo}).subscribe({
      next: (res: RespuestaGenerica) => {
        this.confirmandoTelefono = false;
        if (res?.estado) {
          this.telefonoVerificado = true;
        } else {
          this.navService.setMesage(4, {
            severity: 'error', summary: 'Error',
            detail: res?.mensaje || this.navService.buscarMensaje('ADMINISTRADORSERVICIOSBANCARIOS.MSG_CODIGOINCORRECTO'),
          });
        }
      },
      error: () => {
        this.confirmandoTelefono = false;
        this.navService.setMesage(3);
      },
    });
  }

  confirmarCodigoCorreo(codigo: string): void {
    if (!this.hashOtpCorreo) {
      return;
    }
    this.confirmandoCorreo = true;
    this.plataformaDigitalServicio.validarOtpValidacionIdentidad({hashOtp: this.hashOtpCorreo, codigo}).subscribe({
      next: (res: RespuestaGenerica) => {
        this.confirmandoCorreo = false;
        if (res?.estado) {
          this.correoVerificado = true;
        } else {
          this.navService.setMesage(4, {
            severity: 'error', summary: 'Error',
            detail: res?.mensaje || this.navService.buscarMensaje('ADMINISTRADORSERVICIOSBANCARIOS.MSG_CODIGOINCORRECTO'),
          });
        }
      },
      error: () => {
        this.confirmandoCorreo = false;
        this.navService.setMesage(3);
      },
    });
  }

  guardarContacto(): void {
    this.submitted = true;
    if (!this.puedeGuardar || this.guardandoContacto) {
      return;
    }
    const v = this.formulario.value;
    if ((v.incluirTelefono && this.telefonoDuplicado(v.numeroTelefono)) ||
      (v.incluirCorreo && this.correoDuplicado(v.correoElectronico))) {
      this.mostrarDuplicado();
      return;
    }

    this.guardandoContacto = true;
    const registro: Partial<TerceroUPPasarelaContacto> = {
      idTerceroUPPasarela: this.idTitular,
      nombreContacto: v.nombreContacto,
      numeroTelefono: v.incluirTelefono ? v.numeroTelefono : null,
      correoElectronico: v.incluirCorreo ? v.correoElectronico : null,
      indicadorHabilitado: true,
    };
    this.terceroUpPasarelaContactoServicio.crearContactoVersion(registro).subscribe({
      next: () => {
        this.navService.setMesage(1);
        this.cargarContactos(this.idTitular);
        this.guardandoContacto = false;
        this.cerrarSidebar();
      },
      error: () => {
        this.navService.setMesage(3);
        this.guardandoContacto = false;
      },
    });
  }

  eliminarContacto(contacto: TerceroUPPasarelaContacto): void {
    this.confirmService.confirm({
      message: this.navService.buscarMensaje('ADMINISTRADORSERVICIOSBANCARIOS.MSG_INHABILITARCONTACTO'),
      header: this.navService.buscarMensaje('LBL_CONFIRMACION'),
      icon: 'fa fa-question-circle',
      key: 'admsb-contactos-modal',
      accept: () => {
        this.terceroUpPasarelaContactoServicio.inhabilitarContacto({...contacto, indicadorHabilitado: false}).subscribe({
          next: () => {
            this.navService.setMesage(2);
            this.cargarContactos(this.idTitular);
          },
          error: () => this.navService.setMesage(3),
        });
      },
    });
  }
}
